import { cloneDeep, differenceWith, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  CreateExpensesAction,
  DeleteExpensesAction,
  Expense,
  Expenses,
  FetchExpensesAction,
  ReduxStoreData,
  ServerExpensesUpdate,
  StoreApplicant,
  UpdateExpensesAction,
  UpsertExpensesAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { getExpensesId, sortByCreatedDateAsc, waitForAuth, waitForResourcesToBeCreated } from '../_common';
import {
  createExpenses,
  createExpensesFail,
  createExpensesSuccess,
  deleteExpensesFail,
  deleteExpensesSuccess,
  fetchExpensesFail,
  fetchExpensesSuccess,
  setExpensesError,
  updateExpenses,
  updateExpensesAbort,
  updateExpensesFail,
  updateExpensesSuccess,
  upsertExpensesFail,
  upsertExpensesSuccess,
} from './actions';
import ExpensesAPI from './api';
import { fixExpenses } from './utils';

export function* createExpensesWorker(action: CreateExpensesAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const { clientId, applicantId, expenses } = action.expenses;
    const response = yield call(ExpensesAPI.create, applicationId, applicantId!, expenses);

    yield put(createExpensesSuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      expenses: { clientId },
    } = action;
    yield all([put(createExpensesFail(clientId)), put(setExpensesError(e.response?.data || e.message))]);
  }
}

export function* deleteExpensesWorker(action: DeleteExpensesAction) {
  try {
    yield waitForAuth();
    const { applicationId, applicantId } = action;

    yield call(ExpensesAPI.del, applicationId, applicantId);

    yield put(deleteExpensesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield all([put(deleteExpensesFail(clientId)), put(setExpensesError(e.response?.data || e.message))]);
  }
}

export function* fetchExpensesWorker(action: FetchExpensesAction): any {
  try {
    yield waitForAuth();

    const { applicationId, applicantId } = action;

    let response;
    if (applicantId) {
      response = yield call(ExpensesAPI.fetch, applicationId, applicantId);
    } else {
      response = yield call(ExpensesAPI.fetchAll, applicationId);
    }

    const expenses = response.data.map((expenses: Expenses) => {
      return {
        ...expenses,
        expenses: expenses.expenses
          .map((expense: Expense) => omit(expense, ['meta', 'updatedAt']))
          .sort(sortByCreatedDateAsc),
      };
    });

    yield put(fetchExpensesSuccess(expenses));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchExpensesFail()), put(setExpensesError(e.response?.data || e.message))]);
  }
}

export function* updateExpensesWorker(action: UpdateExpensesAction): any {
  try {
    yield waitForAuth();
    const { applicationId, expenses } = action;
    const { applicantId, clientId, id } = expenses;
    const expensesId = yield getExpensesId(clientId, id);

    // put payload just needs expenses.expenses value
    const putExpensesPayload = expenses.expenses.map((expense: Expense) =>
      omit(expense, ['createdAt', 'isDeidentified', 'isDeleted'])
    ) as ServerExpensesUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!expensesId) {
      yield all([put(updateExpensesAbort()), put(createExpenses(applicationId, expenses))]);
    } else {
      yield call(ExpensesAPI.update, applicationId, applicantId!, putExpensesPayload);

      yield put(updateExpensesSuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      expenses: { clientId },
    } = action;
    yield all([put(updateExpensesFail(clientId)), put(setExpensesError(e.response?.data || e.message))]);
  }
}

export function* upsertExpensesWorker(action: UpsertExpensesAction): any {
  try {
    yield waitForAuth();
    let reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    yield waitForResourcesToBeCreated({
      applicants: reduxStoreApplicants.map((a: StoreApplicant) => a.clientId),
    });
    reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    const { applicationId, expenses } = action;
    const reduxStoreExpenses: Expenses[] = yield select((state: ReduxStoreData) => state.expenses.data);

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedExpensesList = differenceWith(
      // hotfix: clean up undefined amount to ensure the accuracy of comparison
      expenses.map((expense) => ({
        ...expense,
        expenses: expense.expenses.map(({ amount, ...rest }) => (amount != null ? { amount, ...rest } : { ...rest })),
      })),
      reduxStoreExpenses,
      ({ id: id1, clientId: clientId1, ...rest1 }, { id: id2, clientId: clientId2, ...rest2 }) => isEqual(rest1, rest2)
    );

    yield all(
      cloneDeep(changedExpensesList).map((changedExpenses) => {
        fixExpenses(changedExpenses, reduxStoreApplicants);
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const storeExpenses = reduxStoreExpenses.find(
          ({ clientId }: Expenses) => clientId === changedExpenses.clientId
        );

        if (!storeExpenses) {
          // No expenses
          return put(createExpenses(applicationId, changedExpenses));
        } else {
          return put(updateExpenses(applicationId, changedExpenses));
        }
      })
    );
    yield put(upsertExpensesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(upsertExpensesFail()), put(setExpensesError(e.response?.data || e.message))]);
  }
}

export function* expensesWatcher() {
  yield takeEvery(REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES, createExpensesWorker);
  yield takeEvery(REDUX_STORE.EXPENSES.ACTION_TYPES.DELETE_EXPENSES, deleteExpensesWorker);
  yield takeEvery(REDUX_STORE.EXPENSES.ACTION_TYPES.FETCH_EXPENSES, fetchExpensesWorker);
  yield takeEvery(REDUX_STORE.EXPENSES.ACTION_TYPES.UPDATE_EXPENSES, updateExpensesWorker);
  yield takeEvery(REDUX_STORE.EXPENSES.ACTION_TYPES.UPSERT_EXPENSES, upsertExpensesWorker);
}

export default expensesWatcher;
